/**
 * 코스 디렉토리 검증기
 * meta.json 기반으로 코스의 완성도와 일관성을 검증합니다.
 */

class CourseValidator {
    constructor(courseId) {
        this.courseId = courseId;
        this.basePath = `/courses/${courseId}`;
        this.errors = [];
        this.warnings = [];
        this.info = [];
    }

    /**
     * 전체 코스 검증 실행
     */
    async validate() {
        console.log(`🔍 코스 검증 시작: ${this.courseId}`);
        
        try {
            // 1. meta.json 검증
            const metaData = await this.validateMetaJson();
            if (!metaData) return this.getResults();

            // 2. 파일 존재 여부 검증
            await this.validateRequiredFiles(metaData);

            // 3. 스텝별 구조 검증
            await this.validateSteps(metaData);

            // 4. 언어 지원 검증
            await this.validateLanguageSupport(metaData);

            // 5. 체크포인트 및 검증 로직 검증
            await this.validateValidationLogic(metaData);

            console.log(`✅ 코스 검증 완료: ${this.courseId}`);
            
        } catch (error) {
            this.addError('VALIDATION_ERROR', `검증 중 오류 발생: ${error.message}`);
        }

        return this.getResults();
    }

    /**
     * meta.json 파일 검증
     */
    async validateMetaJson() {
        try {
            const response = await fetch(`${this.basePath}/meta.json`);
            if (!response.ok) {
                this.addError('META_MISSING', 'meta.json 파일이 존재하지 않습니다.');
                return null;
            }

            const metaData = await response.json();
            
            // 필수 필드 검증
            const requiredFields = [
                'courseId', 'title', 'description', 'steps', 
                'supportedLanguages', 'defaultLanguage'
            ];

            for (const field of requiredFields) {
                if (!metaData[field]) {
                    this.addError('META_FIELD_MISSING', `meta.json에 필수 필드 '${field}'가 없습니다.`);
                }
            }

            // courseId 일치 검증
            if (metaData.courseId !== this.courseId) {
                this.addError('META_COURSE_ID_MISMATCH', 
                    `meta.json의 courseId(${metaData.courseId})가 디렉토리명(${this.courseId})과 다릅니다.`);
            }

            // 언어 지원 검증
            if (!Array.isArray(metaData.supportedLanguages) || metaData.supportedLanguages.length === 0) {
                this.addError('META_LANGUAGES_INVALID', 'supportedLanguages가 유효하지 않습니다.');
            }

            if (!metaData.supportedLanguages?.includes(metaData.defaultLanguage)) {
                this.addError('META_DEFAULT_LANG_INVALID', 'defaultLanguage가 supportedLanguages에 포함되지 않습니다.');
            }

            this.addInfo('META_LOADED', `meta.json 로드 완료. 스텝 수: ${metaData.steps?.length || 0}`);
            return metaData;

        } catch (error) {
            this.addError('META_PARSE_ERROR', `meta.json 파싱 오류: ${error.message}`);
            return null;
        }
    }

    /**
     * 필수 파일 존재 여부 검증
     */
    async validateRequiredFiles(metaData) {
        const { steps, supportedLanguages } = metaData;

        // Article 파일 검증 (step 1)
        for (const lang of supportedLanguages) {
            const articleFile = lang === 'ko' ? 'article.md' : 'article.en.md';
            const exists = await this.checkFileExists(articleFile);
            
            if (!exists) {
                this.addError('ARTICLE_MISSING', `${lang} 언어용 article 파일(${articleFile})이 없습니다.`);
            } else {
                this.addInfo('ARTICLE_FOUND', `${lang} 언어용 article 파일 확인됨`);
            }
        }

        // 스텝 파일 검증
        for (const step of steps) {
            if (step.type === 'coding') {
                const stepFile = `step${step.id}.json`;
                const exists = await this.checkFileExists(stepFile);
                
                if (!exists) {
                    this.addError('STEP_FILE_MISSING', `스텝 ${step.id} 파일(${stepFile})이 없습니다.`);
                } else {
                    this.addInfo('STEP_FILE_FOUND', `스텝 ${step.id} 파일 확인됨`);
                }
            }
        }
    }

    /**
     * 스텝별 구조 검증
     */
    async validateSteps(metaData) {
        const { steps } = metaData;

        for (const step of steps) {
            if (step.type === 'coding') {
                await this.validateCodingStep(step);
            } else if (step.type === 'article') {
                await this.validateArticleStep(step);
            }
        }
    }

    /**
     * 코딩 스텝 검증
     */
    async validateCodingStep(step) {
        try {
            const response = await fetch(`${this.basePath}/step${step.id}.json`);
            if (!response.ok) {
                this.addError('STEP_NOT_ACCESSIBLE', `스텝 ${step.id} 파일에 접근할 수 없습니다.`);
                return;
            }

            const stepData = await response.json();

            // 필수 구조 검증
            const requiredSections = ['content', 'editor', 'validation'];
            for (const section of requiredSections) {
                if (!stepData[section]) {
                    this.addError('STEP_STRUCTURE_MISSING', 
                        `스텝 ${step.id}에 필수 섹션 '${section}'이 없습니다.`);
                }
            }

            // content 섹션 검증
            if (stepData.content) {
                await this.validateStepContent(step.id, stepData.content);
            }

            // editor 섹션 검증
            if (stepData.editor) {
                await this.validateStepEditor(step.id, stepData.editor);
            }

            // validation 섹션 검증
            if (stepData.validation) {
                await this.validateStepValidation(step.id, stepData.validation);
            }

        } catch (error) {
            this.addError('STEP_PARSE_ERROR', `스텝 ${step.id} 파싱 오류: ${error.message}`);
        }
    }

    /**
     * 스텝 컨텐츠 검증
     */
    async validateStepContent(stepId, content) {
        const languages = ['ko', 'en'];
        
        for (const lang of languages) {
            if (!content[lang]) {
                this.addWarning('STEP_LANG_MISSING', `스텝 ${stepId}에 ${lang} 언어 컨텐츠가 없습니다.`);
                continue;
            }

            const langContent = content[lang];

            // 필수 필드 검증
            const requiredFields = ['header', 'instruction', 'validation'];
            for (const field of requiredFields) {
                if (!langContent[field]) {
                    this.addError('STEP_CONTENT_MISSING', 
                        `스텝 ${stepId} ${lang} 언어에 '${field}' 섹션이 없습니다.`);
                }
            }

            // 체크포인트 검증
            if (langContent.validation?.checkPoints) {
                this.validateCheckPoints(stepId, lang, langContent.validation.checkPoints);
            }

            // 피드백 검증
            if (langContent.feedback) {
                this.validateFeedback(stepId, lang, langContent.feedback);
            }
        }
    }

    /**
     * 에디터 섹션 검증
     */
    async validateStepEditor(stepId, editor) {
        // 필수 필드 검증
        if (!editor.language) {
            this.addError('EDITOR_LANG_MISSING', `스텝 ${stepId}에 editor.language가 없습니다.`);
        }

        if (!editor.starterCode) {
            this.addError('EDITOR_STARTER_MISSING', `스텝 ${stepId}에 starterCode가 없습니다.`);
        }

        // 정답 코드 검증 (solution 또는 modelAnswer)
        const hasSolution = editor.solution || editor.modelAnswer;
        if (!hasSolution) {
            this.addError('EDITOR_SOLUTION_MISSING', 
                `스텝 ${stepId}에 정답 코드(solution 또는 modelAnswer)가 없습니다.`);
        } else {
            this.addInfo('EDITOR_SOLUTION_FOUND', `스텝 ${stepId} 정답 코드 확인됨`);
        }

        // 언어별 코드 검증
        const languages = ['ko', 'en'];
        for (const lang of languages) {
            if (editor.starterCode && !editor.starterCode[lang]) {
                this.addWarning('EDITOR_STARTER_LANG_MISSING', 
                    `스텝 ${stepId}에 ${lang} 언어용 starterCode가 없습니다.`);
            }

            if (hasSolution) {
                const solution = editor.solution?.[lang] || editor.modelAnswer?.[lang];
                if (!solution) {
                    this.addWarning('EDITOR_SOLUTION_LANG_MISSING', 
                        `스텝 ${stepId}에 ${lang} 언어용 정답 코드가 없습니다.`);
                }
            }
        }
    }

    /**
     * 검증 로직 검증
     */
    async validateStepValidation(stepId, validation) {
        // 필수 필드 검증
        const requiredFields = ['requiredVariables', 'variableTypes'];
        for (const field of requiredFields) {
            if (!validation[field]) {
                this.addWarning('VALIDATION_FIELD_MISSING', 
                    `스텝 ${stepId}의 validation에 '${field}'가 없습니다.`);
            }
        }

        // 변수 타입 검증
        if (validation.requiredVariables && validation.variableTypes) {
            for (const variable of validation.requiredVariables) {
                if (!validation.variableTypes[variable]) {
                    this.addWarning('VALIDATION_TYPE_MISSING', 
                        `스텝 ${stepId}에서 변수 '${variable}'의 타입이 정의되지 않았습니다.`);
                }
            }
        }

        // 통과 점수 검증
        if (validation.passingScore && (validation.passingScore < 0 || validation.passingScore > 100)) {
            this.addWarning('VALIDATION_SCORE_INVALID', 
                `스텝 ${stepId}의 passingScore(${validation.passingScore})가 유효하지 않습니다.`);
        }
    }

    /**
     * 체크포인트 검증
     */
    validateCheckPoints(stepId, lang, checkPoints) {
        if (!Array.isArray(checkPoints) || checkPoints.length === 0) {
            this.addError('CHECKPOINT_INVALID', 
                `스텝 ${stepId} ${lang} 언어의 checkPoints가 유효하지 않습니다.`);
            return;
        }

        for (const cp of checkPoints) {
            const requiredFields = ['id', 'label', 'description', 'points'];
            for (const field of requiredFields) {
                if (!cp[field]) {
                    this.addError('CHECKPOINT_FIELD_MISSING', 
                        `스텝 ${stepId} ${lang} 체크포인트에 '${field}'가 없습니다.`);
                }
            }

            if (typeof cp.points !== 'number' || cp.points < 0) {
                this.addWarning('CHECKPOINT_POINTS_INVALID', 
                    `스텝 ${stepId} ${lang} 체크포인트 '${cp.id}'의 점수가 유효하지 않습니다.`);
            }
        }

        this.addInfo('CHECKPOINT_VALIDATED', 
            `스텝 ${stepId} ${lang} 언어 체크포인트 ${checkPoints.length}개 검증 완료`);
    }

    /**
     * 피드백 검증
     */
    validateFeedback(stepId, lang, feedback) {
        const requiredTypes = ['success', 'partial', 'retry'];
        for (const type of requiredTypes) {
            if (!feedback[type]) {
                this.addWarning('FEEDBACK_TYPE_MISSING', 
                    `스텝 ${stepId} ${lang} 언어에 '${type}' 피드백이 없습니다.`);
            } else if (!feedback[type].message || !feedback[type].detail) {
                this.addWarning('FEEDBACK_INCOMPLETE', 
                    `스텝 ${stepId} ${lang} 언어 '${type}' 피드백이 불완전합니다.`);
            }
        }
    }

    /**
     * Article 스텝 검증
     */
    async validateArticleStep(step) {
        // meta.json에서 정의된 파일들 확인
        if (step.files) {
            for (const [lang, filename] of Object.entries(step.files)) {
                const exists = await this.checkFileExists(filename);
                if (!exists) {
                    this.addError('ARTICLE_FILE_MISSING', 
                        `스텝 ${step.id} ${lang} 언어용 파일(${filename})이 없습니다.`);
                }
            }
        }
    }

    /**
     * 언어 지원 검증
     */
    async validateLanguageSupport(metaData) {
        const { supportedLanguages } = metaData;
        
        for (const lang of supportedLanguages) {
            let langSupport = 0;
            let totalSteps = metaData.steps.filter(s => s.type === 'coding').length;

            for (const step of metaData.steps) {
                if (step.type === 'coding') {
                    const stepData = await this.getStepData(step.id);
                    if (stepData?.content?.[lang]) {
                        langSupport++;
                    }
                }
            }

            const supportPercentage = Math.round((langSupport / totalSteps) * 100);
            
            if (supportPercentage < 100) {
                this.addWarning('LANG_SUPPORT_INCOMPLETE', 
                    `${lang} 언어 지원률: ${supportPercentage}% (${langSupport}/${totalSteps})`);
            } else {
                this.addInfo('LANG_SUPPORT_COMPLETE', 
                    `${lang} 언어 지원 완료: 100% (${langSupport}/${totalSteps})`);
            }
        }
    }

    /**
     * 파일 존재 여부 확인
     */
    async checkFileExists(filename) {
        try {
            const response = await fetch(`${this.basePath}/${filename}`);
            return response.ok;
        } catch {
            return false;
        }
    }

    /**
     * 스텝 데이터 가져오기
     */
    async getStepData(stepId) {
        try {
            const response = await fetch(`${this.basePath}/step${stepId}.json`);
            if (!response.ok) return null;
            return await response.json();
        } catch {
            return null;
        }
    }

    /**
     * 오류 추가
     */
    addError(code, message) {
        this.errors.push({ code, message, level: 'error' });
        console.error(`❌ [${code}] ${message}`);
    }

    /**
     * 경고 추가
     */
    addWarning(code, message) {
        this.warnings.push({ code, message, level: 'warning' });
        console.warn(`⚠️ [${code}] ${message}`);
    }

    /**
     * 정보 추가
     */
    addInfo(code, message) {
        this.info.push({ code, message, level: 'info' });
        console.info(`ℹ️ [${code}] ${message}`);
    }

    /**
     * 검증 결과 반환
     */
    getResults() {
        const totalIssues = this.errors.length + this.warnings.length;
        const isValid = this.errors.length === 0;
        
        return {
            courseId: this.courseId,
            isValid,
            summary: {
                errors: this.errors.length,
                warnings: this.warnings.length,
                info: this.info.length,
                totalIssues
            },
            details: {
                errors: this.errors,
                warnings: this.warnings,
                info: this.info
            }
        };
    }
}

export default CourseValidator;